import React, { createContext, useContext, ReactNode } from 'react';

export interface LevelConfig {
  id: string;
  name: string;
  minPoints: number;
  badge: 'bronze' | 'silver' | 'gold' | 'diamond';
  band: 'white' | 'yellow' | 'green' | 'blue' | 'red';
}

// Level configuration defined in frontend
// 20 levels: Bronze (0-4), Silver (5-9), Gold (10-14), Diamond (15-19)
// Each badge type has 5 band colors: white, yellow, green, blue, red
export const LEVEL_CONFIG: LevelConfig[] = [
  // Bronze badge
  { id: 'bronze-white', name: 'Bronze White', minPoints: 0, badge: 'bronze', band: 'white' },
  { id: 'bronze-yellow', name: 'Bronze Yellow', minPoints: 10, badge: 'bronze', band: 'yellow' },
  { id: 'bronze-green', name: 'Bronze Green', minPoints: 25, badge: 'bronze', band: 'green' },
  { id: 'bronze-blue', name: 'Bronze Blue', minPoints: 50, badge: 'bronze', band: 'blue' },
  { id: 'bronze-red', name: 'Bronze Red', minPoints: 100, badge: 'bronze', band: 'red' },
  // Silver badge
  { id: 'silver-white', name: 'Silver White', minPoints: 200, badge: 'silver', band: 'white' },
  { id: 'silver-yellow', name: 'Silver Yellow', minPoints: 400, badge: 'silver', band: 'yellow' },
  { id: 'silver-green', name: 'Silver Green', minPoints: 600, badge: 'silver', band: 'green' },
  { id: 'silver-blue', name: 'Silver Blue', minPoints: 800, badge: 'silver', band: 'blue' },
  { id: 'silver-red', name: 'Silver Red', minPoints: 1000, badge: 'silver', band: 'red' },
  // Gold badge
  { id: 'gold-white', name: 'Gold White', minPoints: 1500, badge: 'gold', band: 'white' },
  { id: 'gold-yellow', name: 'Gold Yellow', minPoints: 2000, badge: 'gold', band: 'yellow' },
  { id: 'gold-green', name: 'Gold Green', minPoints: 2500, badge: 'gold', band: 'green' },
  { id: 'gold-blue', name: 'Gold Blue', minPoints: 3000, badge: 'gold', band: 'blue' },
  { id: 'gold-red', name: 'Gold Red', minPoints: 4000, badge: 'gold', band: 'red' },
  // Diamond badge
  { id: 'diamond-white', name: 'Diamond White', minPoints: 5000, badge: 'diamond', band: 'white' },
  { id: 'diamond-yellow', name: 'Diamond Yellow', minPoints: 6000, badge: 'diamond', band: 'yellow' },
  { id: 'diamond-green', name: 'Diamond Green', minPoints: 7000, badge: 'diamond', band: 'green' },
  { id: 'diamond-blue', name: 'Diamond Blue', minPoints: 8000, badge: 'diamond', band: 'blue' },
  { id: 'diamond-red', name: 'Diamond Red', minPoints: 10000, badge: 'diamond', band: 'red' },
];

interface LevelContextType {
  levels: LevelConfig[];
  getLevelByIndex: (level: number) => LevelConfig | null;
}

const LevelContext = createContext<LevelContextType>({
  levels: LEVEL_CONFIG,
  getLevelByIndex: () => null,
});

export const LevelProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const getLevelByIndex = (level: number): LevelConfig | null => {
    return LEVEL_CONFIG[level] || null;
  };

  return (
    <LevelContext.Provider value={{ levels: LEVEL_CONFIG, getLevelByIndex }}>
      {children}
    </LevelContext.Provider>
  );
};

export const useLevels = () => useContext(LevelContext);